/**
 * People API service for communicating with the Hobbes backend.
 * Provides person-level analytics: list, metrics, detail, qualification updates, CSV export.
 */

import apiClient from './client';
import type { ApiResponse } from './client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PersonSummary {
  id: string;
  email: string;
  name?: string | null;
  phone?: string | null;
  business?: string | null;
  total_sessions: number;
  last_seen_at: string | null;
  first_seen_at: string | null;
  total_time_spent_seconds: number;
  first_source: string | null;
  last_source: string | null;
  last_location: string | null;
  qualification_status: string | null;
  updated_qualification_status: string | null;
  buying_intent: string | null;
}

export interface PeopleListResponse {
  people: PersonSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface PeopleMetricsResponse {
  total_users: number;
  avg_demos_per_user: number;
  high_intent_users: number;
  qualified_leads: number;
  total_users_change?: number | null;
  avg_demos_per_user_change?: number | null;
  high_intent_users_change?: number | null;
  qualified_leads_change?: number | null;
}

export interface PersonSessionSummary {
  id: string;
  started_at: string | null;
  duration_seconds: number | null;
  qualification_status: string | null;
  updated_qualification_status: string | null;
  buying_intent: string | null;
  summary_plaintext: string | null;
  next_steps: string[] | null;
  sentiment: string | null;
  engagement_level: string | null;
  source: string | null;
  location: string | null;
  device: string | null;
  audio_recording_url: string | null;
}

export interface PersonDetailResponse {
  person: PersonSummary;
  sessions: PersonSessionSummary[];
}

export interface PeopleFilters {
  search?: string;
  buying_intent?: string;
  qualification_status?: string;
  source?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

class PeopleService {
  async getPeopleList(
    organizationId: string,
    filters?: PeopleFilters,
  ): Promise<ApiResponse<PeopleListResponse>> {
    const query: Record<string, string> = {};

    if (filters?.search) query.search = filters.search;
    if (filters?.buying_intent) query.buying_intent = filters.buying_intent;
    if (filters?.qualification_status) query.qualification_status = filters.qualification_status;
    if (filters?.source) query.source = filters.source;
    if (filters?.date_from) query.date_from = filters.date_from;
    if (filters?.date_to) query.date_to = filters.date_to;
    if (filters?.limit !== undefined) query.limit = String(filters.limit);
    if (filters?.offset !== undefined) query.offset = String(filters.offset);

    return await apiClient.get<PeopleListResponse>(
      `/api/people/organizations/${organizationId}/list`,
      query,
    );
  }

  async getPeopleMetrics(
    organizationId: string,
    filters?: { date_from?: string; date_to?: string },
  ): Promise<ApiResponse<PeopleMetricsResponse>> {
    const query: Record<string, string> = {};
    if (filters?.date_from) query.date_from = filters.date_from;
    if (filters?.date_to) query.date_to = filters.date_to;

    return await apiClient.get<PeopleMetricsResponse>(
      `/api/people/organizations/${organizationId}/metrics`,
      query,
    );
  }

  async getPersonDetail(
    organizationId: string,
    email: string,
  ): Promise<ApiResponse<PersonDetailResponse>> {
    return await apiClient.get<PersonDetailResponse>(
      `/api/people/organizations/${organizationId}/person`,
      { email },
    );
  }

  async updatePersonQualification(
    organizationId: string,
    email: string,
    qualificationStatus: string,
  ): Promise<ApiResponse<PersonSummary>> {
    return await apiClient.patch<PersonSummary>(
      `/api/people/organizations/${organizationId}/person/qualification`,
      { email, qualification_status: qualificationStatus },
    );
  }

  async exportPeopleCsv(
    organizationId: string,
    filters?: PeopleFilters,
  ): Promise<void> {
    const url = new URL(
      `${apiClient.getBaseUrl()}/api/people/organizations/${organizationId}/export-csv`,
    );

    if (filters?.search) url.searchParams.set('search', filters.search);
    if (filters?.buying_intent) url.searchParams.set('buying_intent', filters.buying_intent);
    if (filters?.qualification_status) url.searchParams.set('qualification_status', filters.qualification_status);
    if (filters?.source) url.searchParams.set('source', filters.source);
    if (filters?.date_from) url.searchParams.set('date_from', filters.date_from);
    if (filters?.date_to) url.searchParams.set('date_to', filters.date_to);

    const token = apiClient.getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(url.toString(), { method: 'GET', headers });
    if (!response.ok) throw new Error('Failed to export CSV');

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = 'people_export.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  }
}

const peopleService = new PeopleService();
export default peopleService;
